import { Pencil, List, Palette, Lock, Unlock, Plus, Minus, FileText, Terminal, BookOpen, MessageCircle, Type, Split, File } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import styles from '@/styles/SectionContextMenu.module.css'

interface SectionContextMenuProps {
  id?: string
  top?: number
  left?: number
  right?: number
  bottom?: number
  isVisible: boolean
  nodeId?: string
  sectionId?: string
  onStateChange?: (state: 'Draft' | 'Detailed' | 'Styled' | 'GrowList' | 'ShrinkList' | 'CreateSubparagraph' | 'ExpandDetail' | 'Explain' | 'ChangeTone' | 'DivideIntoSections' | string) => void
  onProtectChange?: (protectedStatus: boolean) => void
  isProtected?: boolean // Add isProtected prop
  onClose?: () => void // Add this prop
  onCustomCommand?: (command: string) => void
  onCreateDocument?: (sectionContent: string) => void // Add callback for creating document
}

export default function SectionContextMenu({
  id,
  top,
  left,
  right,
  bottom,
  isVisible,
  nodeId,
  sectionId,
  onStateChange,
  onProtectChange,
  isProtected = false,
  onClose,
  onCustomCommand,
  onCreateDocument,
}: SectionContextMenuProps) {
  const [animationClass, setAnimationClass] = useState('')
  const [protectionState, setProtectionState] = useState(isProtected)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customCommand, setCustomCommand] = useState('')

  useEffect(() => {
    setProtectionState(isProtected)
  }, [isProtected])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    if (isVisible) {
      setAnimationClass('')
      requestAnimationFrame(() => {
        setAnimationClass(styles.sectionContextMenuVisible)
      })
    } else {
      setAnimationClass(styles.sectionContextMenuHidden)
      timer = setTimeout(() => {
        setAnimationClass('')
      }, 150)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isVisible, nodeId, sectionId])

  const toggleProtect = () => {
    const newStatus = !protectionState
    setProtectionState(newStatus)
    onProtectChange?.(newStatus)
  }

  const handleStateChange = (state: 'Draft' | 'Detailed' | 'Styled' | 'GrowList' | 'ShrinkList' | 'CreateSubparagraph' | 'ExpandDetail' | 'Explain' | 'ChangeTone' | 'DivideIntoSections' | string ) => {
    if (onStateChange) {
      onStateChange(state)
    }

    if (nodeId && sectionId) {
      const event = new CustomEvent('flowSectionCommand', {
        detail: { nodeId, sectionId, command: state },
      })
      window.dispatchEvent(event)
    }

    // Close menu
    if (onClose) {
      onClose()
    }
  }

  const handleCustomCommandSubmit = () => {
    if (customCommand.trim()) {
      const command = customCommand.trim()
      
      // Call the callback if provided
      if (onCustomCommand) {
        onCustomCommand(command)
      }
      
      // Also dispatch the flowSectionCommand event like other commands
      if (nodeId && sectionId) {
        const event = new CustomEvent('flowSectionCommand', {
          detail: { nodeId, sectionId, command },
        })
        window.dispatchEvent(event)
      }
      
      setCustomCommand('')
      setShowCustomInput(false)
      
      // Close menu
      if (onClose) {
        onClose()
      }
    }
  }

  // OR Option 2: Add a flag to prevent duplicate processing
const handleNewCommand = (command: string) => {
    // Only use one path - prefer the more specific one
    if (nodeId && sectionId) {
      const event = new CustomEvent('flowSectionCommand', {
        detail: { nodeId, sectionId, command },
      })
      window.dispatchEvent(event)
    } else if (onStateChange) {
      onStateChange(command as any)
    }

    // Close menu
    if (onClose) {
      onClose()
    }
}

  if (!isVisible && !animationClass) return null

  return (
    <div
      data-node-id={nodeId}
      data-section-id={sectionId}
      style={{
        position: 'fixed',
        top: top !== undefined ? `${top}px` : undefined,
        left: left !== undefined ? `${left}px` : undefined,
        right: right !== undefined ? `${right}px` : undefined,
        bottom: bottom !== undefined ? `${bottom}px` : undefined,
        zIndex: 1000,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      className={`${styles.sectionContextMenu} ${animationClass}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.buttonContainer}>
        {onStateChange && !protectionState && (
          <>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleStateChange('Draft')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Pencil className="mr-2 h-4 w-4" /> Draft
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleStateChange('Detailed')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <List className="mr-2 h-4 w-4" /> Detailed
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleStateChange('Styled')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Palette className="mr-2 h-4 w-4" /> Styled
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('GrowList')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Plus className="mr-2 h-4 w-4" /> Grow List
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('ShrinkList')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Minus className="mr-2 h-4 w-4" /> Shrink List
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('CreateSubparagraph')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <FileText className="mr-2 h-4 w-4" /> Add Subparagraph
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('ExpandDetail')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <BookOpen className="mr-2 h-4 w-4" /> Expand & Detail
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('Explain')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <MessageCircle className="mr-2 h-4 w-4" /> Explain
            </Button>            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('ChangeTone')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Type className="mr-2 h-4 w-4" /> Change Tone
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleNewCommand('DivideIntoSections')
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Split className="mr-2 h-4 w-4" /> Divide into ::: sections            
            </Button>            
            <Button
              onClick={(e) => {
                e.stopPropagation()
                // Simple direct callback - just like SmartNotesNode pattern
                if (onCreateDocument) {
                  onCreateDocument('') // Flow route will extract the content
                }
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <File className="mr-2 h-4 w-4" /> Create Document
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                setShowCustomInput(!showCustomInput)
              }}
              className={styles.menuButton}
              variant="ghost"
            >
              <Terminal className="mr-2 h-4 w-4" /> Custom Command
            </Button>
            {showCustomInput && (
              <div className="flex gap-2 mt-2 p-2 border-t">
                <Input
                  placeholder="Enter custom command..."
                  value={customCommand}
                  onChange={(e) => setCustomCommand(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Enter') handleCustomCommandSubmit()
                  }}
                  className="flex-1"
                />
                <Button size="sm" onClick={handleCustomCommandSubmit}>
                  Send
                </Button>
              </div>
            )}
          </>
        )}
        {onProtectChange && (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              toggleProtect()
            }}
            className={styles.menuButton}
            variant="ghost"
          >
            {protectionState ? (
              <>
                <Lock className="mr-2 h-4 w-4" /> Unlock editing
              </>
            ) : (
              <>
                <Unlock className="mr-2 h-4 w-4" /> Lock editing
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}
